import type { RuntimeWidgetConfig } from "../../core/config/public-config.types.ts";
import { blueFilterFeatureModule } from "./blue-filter/blue-filter-feature.ts";
import { contrastModeFeatureModule } from "./contrast-mode/contrast-mode-feature.ts";
import { focusEmphasisFeatureModule } from "./focus-emphasis/focus-emphasis-feature.ts";
import { fontFamilyFeatureModule } from "./font-family/font-family-feature.ts";
import {
  featurePanelRegistry,
  getFeaturePanelDefinition,
  type FeaturePanelDefinition,
} from "./feature-panel-registry.ts";
import type { RenderFeatureControlInput } from "./feature-panel.ts";
import { grayscaleFeatureModule } from "./grayscale/grayscale-feature.ts";
import { highlightHeadingsFeatureModule } from "./highlight-headings/highlight-headings-feature.ts";
import { highlightLinksFeatureModule } from "./highlight-links/highlight-links-feature.ts";
import { hideImagesFeatureModule } from "./hide-images/hide-images-feature.ts";
import { largeCursorFeatureModule } from "./large-cursor/large-cursor-feature.ts";
import { letterSpacingFeatureModule } from "./letter-spacing/letter-spacing-feature.ts";
import { lineSpacingFeatureModule } from "./line-spacing/line-spacing-feature.ts";
import { pauseAnimationsFeatureModule } from "./pause-animations/pause-animations-feature.ts";
import { readingGuideFeatureModule } from "./reading-guide/reading-guide-feature.ts";
import { readingModeFeatureModule } from "./reading-mode/reading-mode-feature.ts";
import { textSizeFeatureModule } from "./text-size/text-size-feature.ts";

export type RuntimeFeatureContext = {
  config: unknown;
  definition: FeaturePanelDefinition;
  document: Document;
  featureConfig: RuntimeWidgetConfig["features"][string];
  key: string;
  preference: unknown;
};

export type RuntimeFeatureModule = {
  apply: (context: RuntimeFeatureContext) => void;
  definition: FeaturePanelDefinition;
  isSupported?: (context: RuntimeFeatureContext) => boolean;
  key: string;
  renderControl: (input: RenderFeatureControlInput) => HTMLElement;
  reset: (context: RuntimeFeatureContext) => void;
};

function createPlaceholderControl(document: Document) {
  const element = document.createElement("p");
  element.className = "migrosone-a11y-widget-feature__placeholder";
  element.textContent = "Kontrol Epic 5 kapsamında etkinleştirilecek.";
  return element;
}

export const runtimeFeatureRegistry: RuntimeFeatureModule[] =
  featurePanelRegistry.map((definition) => {
    const implementedModule = [
      textSizeFeatureModule,
      contrastModeFeatureModule,
      blueFilterFeatureModule,
      grayscaleFeatureModule,
      fontFamilyFeatureModule,
      readingModeFeatureModule,
      lineSpacingFeatureModule,
      letterSpacingFeatureModule,
      readingGuideFeatureModule,
      highlightLinksFeatureModule,
      highlightHeadingsFeatureModule,
      focusEmphasisFeatureModule,
      largeCursorFeatureModule,
      hideImagesFeatureModule,
      pauseAnimationsFeatureModule,
    ].find((module) => module.key === definition.key);

    return (
      implementedModule ?? {
        key: definition.key,
        definition,
        renderControl({
          config,
          definition: controlDefinition,
          document,
          key,
          onPreferenceChange,
          preference,
        }) {
          void config;
          void controlDefinition;
          void key;
          void onPreferenceChange;
          void preference;
          return createPlaceholderControl(document);
        },
        apply() {},
        reset() {},
      }
    );
  });

const runtimeRegistryByKey = new Map(
  runtimeFeatureRegistry.map((module) => [module.key, module]),
);

export function getRuntimeFeatureModule(key: string) {
  return runtimeRegistryByKey.get(key);
}
